import type { ReactNode } from 'react'
import React, { createContext, useCallback, useContext as useContextCore, useMemo, useRef, useState } from 'react'
import isEmpty from 'lodash/isEmpty'

type OpenFloatingBubbleFn = () => void

type StoreAiChatOpenContextType = {
  /** The message to auto-send when the chat bubble opens. Undefined means no auto-send. */
  initialMessage: string | undefined
  /** Open the chat bubble with an optional initial message. Called by SearchViaAiButton. */
  open: (initialMessage?: string) => void
  /**
   * Imperative bridge — a thin child of `AiChatBubble` (where the
   * `FloatingChatBubbleProvider` tree is reachable) registers its `open()`
   * function here on mount so this provider can open the bubble for
   * empty-input clicks without requiring an auto-send message.
   */
  registerFloatingOpen: (fn: OpenFloatingBubbleFn | undefined) => void
}

const StoreAiChatOpenContext = createContext<StoreAiChatOpenContextType>({} as StoreAiChatOpenContextType)

type StoreAiChatOpenProviderProps = { children?: ReactNode }

/**
 * Lifts a single piece of shared state — the initial message to seed the AI
 * chat bubble — so the header's "Search via AI" button can trigger the bubble
 * mounted at the layout root. The AiChat component's auto-send effect is
 * ref-deduped, so we clear and re-set the value (via a two-phase setState) to
 * re-arm the bridge when the same keyword is re-submitted.
 *
 * Also maintains a ref to the floating bubble's `open()` function, registered
 * imperatively from a thin child of `AiChatBubble`. This lets
 * `SearchViaAiButton` open the bubble even when the keyword search input is
 * empty, without requiring a full lift of `FloatingChatBubbleProvider`.
 */
export const StoreAiChatOpenProvider = ({ children }: StoreAiChatOpenProviderProps) => {
  const [initialMessage, setInitialMessage] = useState<string | undefined>(undefined)
  const floatingOpenRef = useRef<OpenFloatingBubbleFn | undefined>(undefined)

  const registerFloatingOpen = useCallback((fn: OpenFloatingBubbleFn | undefined) => {
    floatingOpenRef.current = fn
  }, [])

  const open = useCallback((value?: string) => {
    // Always open the floating bubble on click — the button MUST open the
    // AiChatBubble regardless of whether there is typed search text.
    floatingOpenRef.current?.()

    if (isEmpty(value)) {
      // Empty input → no pre-sent message. Clear any stale initialMessage
      // so the AiChat auto-send dedup ref is also reset.
      setInitialMessage(undefined)
      return
    }

    // Clear first so the ref dedup in AiChat re-arms, then set the new value.
    setInitialMessage(undefined)
    setTimeout(() => setInitialMessage(value), 0)
  }, [])

  const contextValue = useMemo(
    () => ({ initialMessage, open, registerFloatingOpen }),
    [initialMessage, open, registerFloatingOpen]
  )

  return <StoreAiChatOpenContext.Provider value={contextValue}>{children}</StoreAiChatOpenContext.Provider>
}

export const useStoreAiChatOpenContext = () => useContextCore(StoreAiChatOpenContext)
